import { useEffect, useRef } from "react";
import { useIsMutating } from "@tanstack/react-query";

import { useSMSFormState } from "./useSMSFormState";
import { usePhoneFormState } from "../../PhoneNumberForm/hooks";
import { useNewAuthorization } from "../../useNewAuthorization";
import { useLoginFormStore } from "../../../../../store/loginFormStore";
import { isNewAuthorization } from "../../../../../store/authorizationStore";
import { ESMSFormState } from "../../../../../store/slices/smsFormSliceCreator";
import { useOTPVerificationMutationReaction } from "./useOTPVerificationMutationReaction";

export const SMS_FORM_ERROR_MESSAGES = {
  empty: "Введите код",
  tooShort: "Не менее 4х цифр",
  incorrect: "Неправильный код",
  unexpectedServerError: "Не удалось отправить код, попробуйте ещё раз или зайдите чуть позже.",
};

let globalMutationInProgress = false;

export function useSMSFormStateReaction() {
  useNewAuthorization("sms");
  const smsForm = useSMSFormState();
  const phoneForm = usePhoneFormState();
  const verifyOtp = useOTPVerificationMutationReaction();
  const mutationRequestIdRef = useRef<string | null>(null);
  const isMutating = useIsMutating({ mutationKey: ["otpVerification"] });

  useEffect(() => {
    if (isNewAuthorization() && useLoginFormStore.getState().smsFormState !== smsForm.formState) {
      return;
    }

    if (isNewAuthorization() && smsForm.formState === ESMSFormState.validated) {
      return;
    }

    switch (smsForm.formState) {
      case ESMSFormState.validating: {
        mutationRequestIdRef.current = null;
        globalMutationInProgress = false;
        if (smsForm.code.length === 0) {
          smsForm.setErrorMessage(SMS_FORM_ERROR_MESSAGES.empty);
          smsForm.setFormState(ESMSFormState.initial);
          return;
        }
        if (smsForm.code.length < (isNewAuthorization() ? 6 : 4)) {
          smsForm.setErrorMessage(isNewAuthorization() ? "Введите 6 цифр" : SMS_FORM_ERROR_MESSAGES.tooShort);
          smsForm.setFormState(ESMSFormState.initial);
          return;
        }
        smsForm.setFormState(ESMSFormState.validated);
        return;
      }
      case ESMSFormState.validated: {
        const requestId = `${phoneForm.getEscapedPhoneNumber()}-${smsForm.code}-${JSON.stringify(smsForm.consents)}`;
        if (mutationRequestIdRef.current === requestId || globalMutationInProgress) {
          return;
        }
        if (isMutating > 0 || verifyOtp.isPending) {
          return;
        }
        mutationRequestIdRef.current = requestId;
        globalMutationInProgress = true;
        smsForm.setFormState(ESMSFormState.submitting);
        verifyOtp.mutate({
          phone: phoneForm.getEscapedPhoneNumber(),
          code: smsForm.code,
          consents: smsForm.consents,
        });
        return;
      }
      case ESMSFormState.initial: {
        mutationRequestIdRef.current = null;
        globalMutationInProgress = false;
        return;
      }
      case ESMSFormState.submitting:
      case ESMSFormState.submitted: {
        return;
      }
    }
  }, [smsForm.formState]);

  useEffect(() => {
    if (verifyOtp.isSuccess || verifyOtp.isError) {
      globalMutationInProgress = false;
    }
  }, [verifyOtp.isSuccess, verifyOtp.isError]);
}
